import {
  deleteValueByFieldName,
  getValueByFieldName,
  setValueByFieldName,
} from './getValueByFieldName';

function isPlainObject(v) {
  return (
    v !== null &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    Object.getPrototypeOf(v) === Object.prototype
  );
}

function isFileLike(v) {
  if (v == null || typeof v !== 'object') return false;
  if (typeof File !== 'undefined' && v instanceof File) return true;
  return typeof Blob !== 'undefined' && v instanceof Blob && typeof v.name === 'string';
}

/**
 * Deep-merge client state onto the server baseline so JSON submits include nested keys
 * that were never touched (plain objects only; arrays and Files replace wholesale).
 */
export function mergeFormSubmissionValues(base, overlay) {
  if (overlay === undefined) {
    return base === undefined ? {} : base;
  }
  if (overlay === null) {
    return null;
  }
  if (isFileLike(overlay)) {
    return overlay;
  }
  if (Array.isArray(overlay)) {
    return overlay;
  }
  if (!isPlainObject(overlay)) {
    return overlay;
  }

  const asPlainBase = isPlainObject(base) ? base : {};
  const result = { ...asPlainBase };
  for (const k of Object.keys(overlay)) {
    const ov = overlay[k];
    const bv = result[k];
    if (
      ov !== undefined &&
      ov !== null &&
      isPlainObject(ov) &&
      bv !== undefined &&
      bv !== null &&
      isPlainObject(bv)
    ) {
      result[k] = mergeFormSubmissionValues(bv, ov);
    } else if (ov !== undefined) {
      result[k] = ov;
    }
  }
  return result;
}

function defaultForLeafField(f) {
  const t = (f.template || '').replace('.html', '');
  if (t === 'checkbox' || t === 'checkbox_cards' || t === 'null_checkbox') return false;
  if (t === 'json') return null;
  if (t === 'rating') return null;
  return '';
}

/**
 * Ensure every writable field from the schema has a value when it was absent from both
 * server values and client state (e.g. optional fields omitted in the initial payload).
 * @param {{ insideNested?: boolean }} ctx - Leaf fields under `nested` / many-nested rows default to ''.
 */
function fillMissingWritableFields(fields, out, ctx = {}) {
  if (!fields || typeof fields !== 'object' || out == null) return;
  const insideNested = Boolean(ctx.insideNested);

  for (const key of Object.keys(fields)) {
    const f = fields[key];
    if (!f || f.readOnly) continue;

    if (f.template === 'nested' && f.fields) {
      fillMissingWritableFields(f.fields, out, { insideNested: true });
    } else if (f.template === 'many_nested_objects') {
      const nk = f.fieldName || key;
      let arr = getValueByFieldName(out, nk);
      if (arr === undefined) {
        const initial = f.value;
        arr = Array.isArray(initial) ? [...initial] : [];
        setValueByFieldName(out, nk, arr);
      }
      if (f.fields && Array.isArray(arr)) {
        arr.forEach((item) => {
          if (item != null && typeof item === 'object' && !Array.isArray(item)) {
            fillMissingWritableFields(f.fields, item, { insideNested: true });
          }
        });
      }
    } else if (f.fieldName) {
      const currentValue = getValueByFieldName(out, f.fieldName);
      const tmpl = (f.template || '').replace('.html', '');
      const isCheckbox =
        tmpl === 'checkbox' || tmpl === 'checkbox_cards' || tmpl === 'null_checkbox';

      // For required checkboxes, treat null as "missing" and default to false.
      const shouldDefault =
        currentValue === undefined || (isCheckbox && currentValue === null && f.allowNull === false);

      if (shouldDefault) {
        let def;
        if (f.value !== undefined) {
          def = f.value;
        } else if (insideNested) {
          // Nested objects are pruned for empty values; checkboxes must default to false
          // so "unchecked" isn't pruned away as ''.
          if (isCheckbox) {
            def = defaultForLeafField(f);
          } else {
            def = '';
          }
        } else {
          def = defaultForLeafField(f);
        }
        setValueByFieldName(out, f.fieldName, def);
      }
    }
  }
}

/**
 * Remove keys whose values are "empty": null, '', or plain objects/arrays whose contents
 * are only empty (recursively). Keeps false, 0, and File/Blob payloads.
 * Used only for nested / many-nested subtrees, not the whole form.
 */
function pruneEmptyPayloadNodes(value) {
  if (value === null || value === '') {
    return undefined;
  }
  if (typeof value !== 'object') {
    return value;
  }
  if (isFileLike(value)) {
    return value;
  }
  if (Array.isArray(value)) {
    const next = [];
    for (const item of value) {
      const pruned = pruneEmptyPayloadNodes(item);
      if (pruned !== undefined) {
        next.push(pruned);
      }
    }
    return next.length === 0 ? undefined : next;
  }
  if (!isPlainObject(value)) {
    return value;
  }
  const out = {};
  for (const k of Object.keys(value)) {
    const pruned = pruneEmptyPayloadNodes(value[k]);
    if (pruned !== undefined) {
      out[k] = pruned;
    }
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

/**
 * Strip all-null/'' (and recursively empty) structures only for `nested` and
 * `many_nested_objects` blocks and their nested children — top-level leaf fields stay as-is.
 */
function pruneNestedEmptySubtrees(fields, out) {
  if (!fields || typeof fields !== 'object' || out == null) return;

  for (const key of Object.keys(fields)) {
    const f = fields[key];
    if (!f) continue;

    if (f.template === 'nested' && f.fields) {
      const nk = f.fieldName || key;
      const cur = getValueByFieldName(out, nk);
      if (cur != null && typeof cur === 'object' && !Array.isArray(cur) && isPlainObject(cur)) {
        const pruned = pruneEmptyPayloadNodes(cur);
        if (pruned === undefined) {
          deleteValueByFieldName(out, nk);
        } else {
          setValueByFieldName(out, nk, pruned);
        }
      }
      pruneNestedEmptySubtrees(f.fields, out);
    } else if (f.template === 'many_nested_objects' && f.fields) {
      const nk = f.fieldName || key;
      const arr = getValueByFieldName(out, nk);
      if (Array.isArray(arr)) {
        arr.forEach((item) => {
          if (item != null && typeof item === 'object' && !Array.isArray(item) && isPlainObject(item)) {
            pruneNestedEmptySubtrees(f.fields, item);
          }
        });
        const next = arr
          .map((item) =>
            item != null && typeof item === 'object' && !Array.isArray(item) && isPlainObject(item)
              ? pruneEmptyPayloadNodes(item)
              : item
          )
          .filter((v) => v !== undefined);
        if (next.length === 0) {
          deleteValueByFieldName(out, nk);
        } else {
          setValueByFieldName(out, nk, next);
        }
      }
    }
  }
}

/**
 * Build the full object to JSON-serialize: baseline from the server form, overlay client
 * state, then any schema-defined keys still missing.
 */
export function buildCompleteFormPayload(form, stateValues) {
  const merged = mergeFormSubmissionValues(form?.values || {}, stateValues || {});
  fillMissingWritableFields(form?.fields, merged);
  pruneNestedEmptySubtrees(form?.fields, merged);
  return merged;
}
